/**
 * Product Card Subscription V2 - Updated Design
 * Handles informational pricing display and AJAX cart functionality
 */

class ProductCardSubscriptionV2 {
  constructor(cardElement) {
    this.card = cardElement;
    this.productId = this.card.dataset.productId;
    this.cardId = this.card.dataset.cardId;
    this.hasSubscription = this.card.dataset.hasSubscription === 'true';
    this.selectedColorScheme = this.card.dataset.selectedColorScheme;
    this.salePercent = this.card.dataset.ihSalePercent ? parseInt(this.card.dataset.ihSalePercent) : 0;
    
    // Elements
    this.elements = {
      pricingColumns: this.card.querySelectorAll('[data-option]'),
      subscribeColumn: this.card.querySelector('[data-option="subscription"]'),
      onetimeColumn: this.card.querySelector('[data-option="onetime"]'),
      subscribePrice: this.card.querySelector('[data-subscribe-price]'),
      onetimePrice: this.card.querySelector('[data-onetime-price]'),
      singlePrice: this.card.querySelector('.pcm-card-v2__price-display'),
      planSelector: this.card.querySelector('.pcm-card-v2__plan-selector'),
      planSelect: this.card.querySelector('[data-plan-selector]'),
      variantSelect: this.card.querySelector('[data-variant-selector]'),
      addToCartBtn: this.card.querySelector('[data-add-to-cart]'),
      ctaText: this.card.querySelector('[data-cta-text]'),
      ctaPrice: this.card.querySelector('[data-cta-price]'),
      spinner: this.card.querySelector('[data-spinner]'),
      errorMessage: this.card.querySelector('[data-error-message]'),
      productIdInput: this.card.querySelector('[data-product-id]'),
      variantIdInput: this.card.querySelector('[data-variant-id]'),
      sellingPlanIdInput: this.card.querySelector('[data-selling-plan-id]'),
      quantityInputHidden: this.card.querySelector('[data-quantity]')
    };
    
    // State
    this.state = {
      purchaseType: 'onetime', // Default to one-time purchase
      selectedVariantId: this.elements.variantIdInput?.value,
      selectedPlanId: null,
      quantity: 1,
      isLoading: false
    };
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateInitialState();
  }

  bindEvents() {
    // Pricing column clicks
    this.elements.pricingColumns.forEach(column => {
      column.addEventListener('click', (e) => this.handleColumnClick(e));
    });

    // Plan selector
    if (this.elements.planSelect) {
      this.elements.planSelect.addEventListener('change', (e) => this.handlePlanChange(e));
    }

    // Variant selector
    if (this.elements.variantSelect) {
      this.elements.variantSelect.addEventListener('change', (e) => this.handleVariantChange(e));
    }

    // Add to cart
    if (this.elements.addToCartBtn) {
      this.elements.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  updateInitialState() {
    // Check initial variant availability
    this.checkVariantAvailability();
    
    // For products without subscriptions, always use one-time purchase
    if (!this.hasSubscription) {
      this.state.purchaseType = 'onetime';
      this.state.selectedPlanId = null;
      this.updateVisualState();
      this.updatePricing();
      return;
    }
    
    // For subscription products, default to one-time (user preference)
    this.state.purchaseType = 'onetime';
    
    // Set initial selling plan ID if available
    if (this.elements.planSelect) {
      const firstOption = this.elements.planSelect.options[0];
      if (firstOption) {
        this.state.selectedPlanId = firstOption.value;
      }
    }
    
    this.updateVisualState();
    this.updatePricing();
  }

  getDebugData() {
    const debugInputs = {};
    const inputs = this.card.querySelectorAll('[data-has-subscription], [data-subscription-price], [data-onetime-price], [data-allocation-found], [data-selling-plan-allocations-count]');
    inputs.forEach(input => {
      const key = Array.from(input.attributes).find(attr => attr.name.startsWith('data-'))?.name;
      if (key) {
        debugInputs[key] = input.value;
      }
    });
    return debugInputs;
  }

  checkVariantAvailability() {
    // Check if current variant is available
    if (this.elements.variantSelect) {
      const selectedOption = this.elements.variantSelect.selectedOptions[0];
      const isAvailable = selectedOption?.dataset.available === 'true';
      this.elements.addToCartBtn.disabled = !isAvailable;
    } else {
      // For single-variant products, check from card dataset (server-side set)
      const isAvailable = this.card.dataset.available === 'true';
      this.elements.addToCartBtn.disabled = !isAvailable;
    }
    
    // Also update the CTA text immediately
    this.updateCTAText();
  }

  handleColumnClick(e) {
    const column = e.currentTarget;
    const purchaseType = column.dataset.option;
    
    if (purchaseType === this.state.purchaseType) return;
    
    this.state.purchaseType = purchaseType;
    this.updateVisualState();
    this.updatePricing();
    
    // Update selling plan state
    if (purchaseType === 'subscription' && this.hasSubscription && this.elements.planSelect) {
      const selectedOption = this.elements.planSelect.selectedOptions[0];
      this.state.selectedPlanId = selectedOption?.value || null;
    } else {
      this.state.selectedPlanId = null;
    }
    
    // Update hidden input
    if (this.elements.sellingPlanIdInput) {
      this.elements.sellingPlanIdInput.value = this.state.selectedPlanId || '';
    }
    
    // Dispatch custom event
    this.dispatchEvent('optionChanged', {
      purchaseType,
      productId: this.productId,
      variantId: this.state.selectedVariantId
    });
  }

  updateVisualState() {
    // Update column visual states
    this.elements.pricingColumns.forEach(column => {
      const optionType = column.dataset.option;
      const isSelected = optionType === this.state.purchaseType;
      
      column.classList.toggle('pcm-card-v2__pricing-column--selected', isSelected);
      
      // Apply or remove color scheme class
      if (this.selectedColorScheme) {
        if (isSelected) {
          column.classList.add(`color-${this.selectedColorScheme}`);
        } else {
          column.classList.remove(`color-${this.selectedColorScheme}`);
        }
      }
    });
    
    // Show/hide plan selector for subscription
    if (this.elements.planSelector) {
      const showPlanSelector = this.state.purchaseType === 'subscription' && this.hasSubscription;
      this.elements.planSelector.classList.toggle('active', showPlanSelector);
    }
    
    // Show/hide variant selector if needed
    if (this.elements.variantSelect && this.elements.variantSelect.parentElement) {
      const showVariants = this.elements.variantSelect.options.length > 1;
      this.elements.variantSelect.parentElement.classList.toggle('active', showVariants);
    }
  }

  handlePlanChange(e) {
    const selectedOption = e.target.selectedOptions[0];
    this.state.selectedPlanId = selectedOption.value;

    if (this.elements.sellingPlanIdInput) {
      this.elements.sellingPlanIdInput.value = this.state.selectedPlanId;
    }

    // Update subscription price from plan data
    const subscriptionPrice = selectedOption.dataset.price;
    if (subscriptionPrice && this.elements.subscribePrice) {
      this.elements.subscribePrice.innerHTML = this.formatMoney(subscriptionPrice);
    }

    // Update delivery frequency text from plan name
    const planName = selectedOption.dataset.planName;
    if (planName) {
      const frequencyTextElement = this.card.querySelector('[data-frequency-text]');
      if (frequencyTextElement) {
        // Extract only the delivery interval part, removing discount text
        // Example: "Delivery every 60 days with 15% discount" -> "Delivery every 60 days"
        const cleanedPlanName = planName.replace(/\s+with\s+\d+%\s+discount$/i, '').trim();
        frequencyTextElement.textContent = cleanedPlanName;
      }
    }

    this.updatePricing();
  }

  handleVariantChange(e) {
    const selectedOption = e.target.selectedOptions[0];
    this.state.selectedVariantId = selectedOption.value;
    
    if (this.elements.variantIdInput) {
      this.elements.variantIdInput.value = this.state.selectedVariantId;
    }
    
    // Update availability
    const isAvailable = selectedOption.dataset.available === 'true';
    this.elements.addToCartBtn.disabled = !isAvailable;
    
    this.updatePricing();
    this.updateCTAText(); // Ensure CTA text updates when variant changes
  }

  updatePricing() {
    const variantSelect = this.elements.variantSelect;
    const planSelect = this.elements.planSelect;

    let subscriptionPrice, onetimePrice;

    // Get one-time price from variant selector (always up-to-date on variant change)
    if (variantSelect) {
      const selectedVariant = variantSelect.selectedOptions[0];
      onetimePrice = selectedVariant?.dataset.price;
    } else {
      // Single-variant product: read from hidden input (won't change)
      const onetimePriceInput = this.card.querySelector('[data-onetime-price]');
      onetimePrice = onetimePriceInput?.value;
    }

    // Get subscription price from plan selector or fall back to one-time price
    if (this.hasSubscription && planSelect) {
      const selectedPlan = planSelect.selectedOptions[0];
      subscriptionPrice = selectedPlan?.dataset.price || onetimePrice;
    } else {
      subscriptionPrice = onetimePrice;
    }
    
    // For products with subscription options
    if (this.hasSubscription) {
      // Update subscribe price display
      if (this.elements.subscribePrice && subscriptionPrice) {
        this.elements.subscribePrice.innerHTML = this.formatMoney(subscriptionPrice);
      }

      // Update one-time price display (with sale discount if active)
      if (this.elements.onetimePrice && onetimePrice) {
        this.elements.onetimePrice.innerHTML = this.formatOnetimePrice(onetimePrice);
      }
    } else {
      // For one-time only products, update the single price display
      if (this.elements.singlePrice && onetimePrice) {
        this.elements.singlePrice.innerHTML = this.formatOnetimePrice(onetimePrice);
      }
    }

    this.updateCTAText();
  }

  updateCTAText() {
    if (!this.elements.ctaText) return;

    let isAvailable = true;

    // Check if sold out
    if (this.elements.variantSelect) {
      const selectedVariant = this.elements.variantSelect.selectedOptions[0];
      isAvailable = selectedVariant?.dataset.available === 'true';
    } else {
      // For single-variant products, check from card dataset (server-side set)
      isAvailable = this.card.dataset.available === 'true';
    }

    if (!isAvailable) {
      this.elements.ctaText.textContent = 'Sold Out';
      this.elements.addToCartBtn.disabled = true;
      this.elements.addToCartBtn.classList.add('sold-out');
      return;
    }

    // Available - reset button state
    this.elements.addToCartBtn.disabled = false;
    this.elements.addToCartBtn.classList.remove('sold-out');

    // Pre-Order Button override: use custom label (no price) when set on this card
    const preorderText = this.card.dataset.preorderText;
    this.elements.ctaText.textContent = preorderText || 'Add to Cart';
    if (preorderText) return;

    // Update CTA price from the currently selected column's displayed price
    const ctaPriceEl = this.card.querySelector('[data-cta-price]');
    if (ctaPriceEl) {
      const selectedColumn = this.card.querySelector('.pcm-card-v2__pricing-column--selected');
      if (selectedColumn) {
        const priceEl = selectedColumn.querySelector('.pcm-card-v2__pricing-price');
        if (priceEl) {
          // For one-time with sale, the price element contains discounted + strikethrough;
          // extract just the first text/span (the sale price) for the CTA
          const salePrice = priceEl.querySelector('.pcm-card-v2__pricing-compare');
          if (salePrice) {
            // Has strikethrough — clone, remove the compare span, use what's left
            const clone = priceEl.cloneNode(true);
            clone.querySelector('.pcm-card-v2__pricing-compare')?.remove();
            ctaPriceEl.innerHTML = clone.innerHTML.trim();
          } else {
            ctaPriceEl.innerHTML = priceEl.innerHTML.trim();
          }
        }
      }
    }
  }

  async handleAddToCart() {
    // Prevent add to cart if disabled or out of stock
    if (this.state.isLoading || this.elements.addToCartBtn.disabled) {
      return;
    }
    
    this.setLoadingState(true);
    
    try {
      // Prepare cart data - keep it simple
      const cartData = {
        id: parseInt(this.state.selectedVariantId),
        quantity: parseInt(this.state.quantity)
      };
      
      // Add selling plan for subscription
      if (this.state.purchaseType === 'subscription' && this.state.selectedPlanId) {
        cartData.selling_plan = parseInt(this.state.selectedPlanId);
      }

      // Use Shopify's cart add API directly
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      });
      
      if (response.ok) {
        const result = await response.json();

        // Show success state
        this.showSuccessState();
        
        // Update cart without page refresh
        await this.updateCartState();
        
      } else {
        // Handle error response
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.description || 'Failed to add to cart');
      }

    } catch (error) {
      this.showError(error.message || 'Unable to add to cart. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }


  async updateCartState() {
    try {
      // Get current cart data
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();

        // Update cart count badge in header
        // Directly update all <cart-count> elements to match the theme's CartCount
        // custom element behavior (see theme.js). We avoid publishing PubSub cartUpdate
        // here because CartItems.onCartUpdate expects sections HTML in the payload,
        // which /cart.js does not provide. The cart:refresh event (below) handles
        // the drawer content refresh separately.
        this.updateCartCount(cartData.item_count);

        // Also dispatch cart:updated DOM event for any other listeners
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: cartData }
          })
        );

        // Update cart drawer content
        await this.refreshCartDrawer();

        // Open cart drawer
        this.openCartDrawer();
        
      }
    } catch (error) {
    }
  }

  updateCartCount(itemCount) {
    // Update all <cart-count> custom elements in the header and mobile bar
    // Mirrors the logic in CartCount.onCartUpdate (theme.js)
    const cartCountElements = document.querySelectorAll('cart-count');
    cartCountElements.forEach(element => {
      let count = itemCount > 99 ? '99+' : itemCount;
      const type = element.dataset.type;

      if (type === 'blank') {
        count = `(${count})`;
      }

      if (element.classList.contains('cart-count--absolute')) {
        if (itemCount > 99) {
          element.innerHTML = `<span class="text-sm-extra">${count}</span>`;
          element.classList.add('cart-count--small-medium');
        } else {
          element.innerText = count;
          element.classList.remove('cart-count--small-medium');
        }
      } else {
        element.innerText = count;
      }

      element.hidden = itemCount === 0;
    });

    // Update document-level cart state class
    const method = itemCount === 0 ? 'remove' : 'add';
    document.documentElement.classList[method]('cart-has-items');
  }

  async refreshCartDrawer() {
    // Find cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Trigger cart refresh event that the theme listens for
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: {
            open: true,
          },
        })
      );
      // Add a global click handler for cart drawer close buttons
      // This will work regardless of what happens during refresh
      this.addGlobalCloseButtonHandler();
    }
  }

  addGlobalCloseButtonHandler() {
    // Use the static method
    ProductCardSubscriptionV2.addGlobalCloseButtonHandler();
  }


  openCartDrawer() {
    // Find and open cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.show === 'function') {
      setTimeout(() => {
        cartDrawer.show(this.elements.addToCartBtn);
      }, 200);
    }
  }

  showSuccessState() {
    // Show brief success state
    this.elements.addToCartBtn.classList.add('success');
    this.elements.ctaText.textContent = 'Added!';
    
    setTimeout(() => {
      this.elements.addToCartBtn.classList.remove('success');
      this.updateCTAText();
    }, 2000);
  }

  setLoadingState(isLoading) {
    this.state.isLoading = isLoading;
    this.elements.addToCartBtn.classList.toggle('loading', isLoading);
    
    if (isLoading) {
      this.elements.spinner.style.display = 'block';
    } else {
      this.elements.spinner.style.display = 'none';
    }
  }

  showError(message) {
    if (this.elements.errorMessage) {
      this.elements.errorMessage.textContent = message;
      this.elements.errorMessage.style.display = 'block';
      
      setTimeout(() => {
        this.elements.errorMessage.style.display = 'none';
      }, 5000);
    }
  }

  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true
    });
    
    this.card.dispatchEvent(event);
  }

  formatMoney(cents) {
    return FoxTheme.Currency.formatMoney(parseInt(cents), FoxTheme.settings.moneyFormat);
  }

  getSalePrice(cents) {
    if (this.salePercent > 0) {
      const originalCents = parseInt(cents);
      return Math.round(originalCents - (originalCents * this.salePercent / 100));
    }
    return parseInt(cents);
  }

  formatOnetimePrice(cents) {
    if (this.salePercent > 0) {
      const originalCents = parseInt(cents);
      const discountedCents = Math.round(originalCents - (originalCents * this.salePercent / 100));
      return this.formatMoney(discountedCents) +
        ' <s class="pcm-card-v2__pricing-compare">' + this.formatMoney(originalCents) + '</s>';
    }
    return this.formatMoney(cents);
  }
}

// Initialize all product cards when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const cards = document.querySelectorAll('.pcm-card-v2');
  cards.forEach(card => {
    new ProductCardSubscriptionV2(card);
  });
  
  // Add global close button handler for cart drawer
  ProductCardSubscriptionV2.addGlobalCloseButtonHandler();
});

// Static method to add global close button handler
ProductCardSubscriptionV2.addGlobalCloseButtonHandler = function() {
  // Remove any existing global handler first
  if (this.globalCloseButtonHandler) {
    document.removeEventListener('click', this.globalCloseButtonHandler);
  }
  
  // Create the handler function
  this.globalCloseButtonHandler = (event) => {
    // Check if the clicked element or its parent is a cart drawer close button
    const target = event.target.closest('.drawer__close-btn');
    if (target) {
      // Double-check this is specifically the CartDrawer close button
      if (target.getAttribute('aria-controls') === 'CartDrawer') {
        const cartDrawer = target.closest('cart-drawer');
        if (cartDrawer) {
          event.preventDefault();
          event.stopPropagation();
          cartDrawer.hide();
        }
      }
    }
  };
  
  // Add the global handler
  document.addEventListener('click', this.globalCloseButtonHandler);
};

// Handle dynamic content (e.g., AJAX loaded products)
const observerV2 = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const cards = node.querySelectorAll ? node.querySelectorAll('.pcm-card-v2') : [];
        cards.forEach(card => {
          if (!card.pcmV2Instance) {
            card.pcmV2Instance = new ProductCardSubscriptionV2(card);
          }
        });
      }
    });
  });
});

observerV2.observe(document.body, {
  childList: true,
  subtree: true
});

// Export for external use
window.ProductCardSubscriptionV2 = ProductCardSubscriptionV2;